import React, { useEffect, useRef, useState } from 'react';

import { subscribeDoseGapPrompt } from '../../utils/helper/doseGapPrompt';

/**
 * Confirmation shown when a dose is handed over before the prescription's
 * required separation has elapsed.
 *
 * Every string comes from the backend's dose_gap_prompt, so the wording and the
 * rule stay in one place. A reason is optional: the backend records who
 * delivered early and by how much either way, and demanding a sentence at the
 * patient's door only earns "n/a".
 *
 * Laid out for a phone held in one hand at a patient's door: a sheet on the
 * bottom edge where a thumb reaches, becoming a centred dialog once there is
 * room for one. Mounted once at the app root; every delivery screen raises it
 * through doseGapPrompt.
 */
const DoseGapOverrideModal = () => {
    const [prompt, setPrompt] = useState(null);
    const resolverRef = useRef(null);

    useEffect(() => subscribeDoseGapPrompt((nextPrompt, resolve) => {
        resolverRef.current = resolve;
        setPrompt(nextPrompt);
    }), []);

    // A dose decision should not be dismissed by a stray tap, so the backdrop
    // is inert and only the buttons close this.
    const close = (result) => {
        const resolve = resolverRef.current;
        resolverRef.current = null;
        setPrompt(null);
        if (resolve) resolve(result);
    };

    // Deliberately no body scroll lock, and one that clears any lock left
    // behind. Locking the body stopped the page scrolling behind the sheet, but
    // every path that skipped the cleanup - an unmount mid-flight, another
    // modal restoring its own captured value - left the whole page frozen with
    // no way back. A background that scrolls a little is not worth a driver
    // stranded on a dead screen. overscroll-contain below keeps a scroll inside
    // the sheet from reaching the page.
    useEffect(() => {
        if (!prompt) return;
        if (document.body.style.overflow === 'hidden') {
            document.body.style.overflow = '';
        }
    }, [prompt]);

    if (!prompt) return null;

    // The backend used to send each row as a sentence and now sends it split.
    // The apps and the servers deploy separately, so both shapes are accepted -
    // otherwise a new app against an older server shows an empty dialog.
    const details = (Array.isArray(prompt.details) ? prompt.details : [])
        .map((row) => (typeof row === 'string' ? { label: row, value: '' } : row))
        .filter((row) => row && (row.label || row.value));

    // Absent means overridable, so an older payload still behaves as before.
    const canOverride = prompt.can_override !== false;
    const title = prompt.title || 'Too early for this dose';
    const confirmLabel = prompt.confirm_label || 'Deliver Anyway';
    const cancelLabel = prompt.cancel_label || (canOverride ? 'Cancel' : 'Close');

    return (
        <div className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="dose-gap-title"
                className="flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white
                    shadow-xl sm:max-w-sm sm:rounded-2xl">

                {/* Reads as a sheet that can be pulled up from the bottom edge. */}
                <div className="flex justify-center pt-2 sm:hidden" aria-hidden="true">
                    <span className="h-1 w-10 rounded-full bg-gray-300" />
                </div>

                <div className="flex items-start gap-3 px-5 pt-4 sm:pt-5">
                    <div className={`mt-[2px] flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                        ${canOverride ? 'bg-amber-100' : 'bg-red-100'}`}>
                        <svg
                            className={`h-6 w-6 ${canOverride ? 'text-amber-600' : 'text-red-600'}`}
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 7v5l3 2" />
                        </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 id="dose-gap-title" className="text-[17px] font-semibold leading-snug text-gray-900">
                            {title}
                        </h2>
                        {prompt.message ? <p className="mt-1 text-[14px] leading-snug text-gray-600">{prompt.message}</p> : null}
                    </div>
                </div>

                {details.length > 0 ? (
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-4">
                        <dl className="divide-y divide-gray-100 rounded-xl bg-gray-50 px-3.5">
                            {details.map((row) => (
                                <div key={row.label} className="flex items-baseline justify-between gap-4 py-2.5">
                                    <dt className="shrink-0 text-[13px] text-gray-500">{row.label}</dt>
                                    <dd className="text-right text-[14px] font-medium text-gray-900">{row.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                ) : null}

                {/* Below the hard floor the backend refuses however the request
                    is sent, so there is no button to offer - showing one would
                    promise something the server will not do.

                    Stacked on a phone so each target is full width and the
                    destructive one is not where a thumb rests by default. */}
                <div
                    className="mt-4 flex flex-col-reverse gap-2 border-t border-gray-100 px-5 pt-4 pb-4 sm:flex-row"
                    style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
                    <button
                        type="button"
                        onClick={() => close({ confirmed: false, reason: '' })}
                        className="min-h-[48px] w-full rounded-xl border border-gray-300 text-[15px] font-medium
                            text-gray-700 active:bg-gray-50 sm:flex-1">
                        {cancelLabel}
                    </button>
                    {canOverride ? (
                        <button
                            type="button"
                            onClick={() => close({ confirmed: true, reason: '' })}
                            className="min-h-[48px] w-full rounded-xl bg-amber-500 text-[15px] font-medium text-white
                                active:bg-amber-600 sm:flex-1">
                            {confirmLabel}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default DoseGapOverrideModal;
